import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

const baseSeo = {
  title: 'MC3 Smart Tech | Custom Software, Cloud & Cybersecurity Studio',
  description:
    'MC3 Smart Tech delivers full-stack product engineering, cloud-native platforms, and cybersecurity programs that help ambitious organisations scale securely.',
  keywords:
    'MC3 Smart Tech, software development UK, cloud consulting, cybersecurity engineers, digital product studio, AI integrations, enterprise apps',
  image: '/logo192.png',
  type: 'website',
};

const seoConfig = {
  '/': {
    title: 'Digital Innovation Partner',
    description:
      'Strategy, design, engineering, and managed services under one roof. Launch bold experiences faster with MC3 Smart Tech.',
  },
  '/home': {
    title: 'Digital Innovation Partner',
  },
  '/about-us': {
    title: 'About Our Product Studio',
    description:
      'Meet the UK-founded MC3 Smart Tech team accelerating transformation for regulated industries with transparent collaboration.',
  },
  '/our-services': {
    title: 'Engineering & Consulting Services',
    description:
      'Full-stack development, CX-led design, cloud architecture, QA automation, cybersecurity programs, and 24/7 support.',
  },
  '/expertize': {
    title: 'Industry Expertise',
    description:
      'Trusted across fintech, health, mobility, and public sector projects thanks to deep domain knowledge and compliance rigor.',
  },
  '/support': {
    title: 'Dedicated Support & Success',
    description:
      'Flexible SLAs, proactive monitoring, and multilingual engineers keep every MC3 Smart Tech deployment resilient.',
  },
  '/contact-us': {
    title: 'Book a Strategy Call',
    description:
      'Share your goals and our consultants will design a roadmap that blends modern UX, AI, and secure infrastructure.',
  },
  '/privacy-policy': {
    title: 'Privacy Policy',
    description: 'Understand how MC3 Smart Tech handles data protection and GDPR compliance for every engagement.',
  },
  '/terms-of-service': {
    title: 'Terms Of Service',
  },
  '/careers': {
    title: 'Careers',
    description:
      'Join a multidisciplinary team of engineers, designers, and strategists building high-impact products across the globe.',
  },
};

const updateTag = (selector, attributes) => {
  let element = document.querySelector(selector);
  if (!element) {
    element = document.createElement(attributes.tagName || 'meta');
    if (selector.includes('name=')) {
      element.setAttribute('name', attributes.name);
    } else if (selector.includes('property=')) {
      element.setAttribute('property', attributes.property);
    } else if (selector.startsWith('link')) {
      Object.entries(attributes).forEach(([key, value]) => {
        if (key !== 'tagName') element.setAttribute(key, value);
      });
    }
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    if (key !== 'tagName') element.setAttribute(key, value);
  });
  return element;
};

const SeoManager = () => {
  const location = useLocation();

  const seo = useMemo(() => {
    return {
      ...baseSeo,
      ...seoConfig[location.pathname],
    };
  }, [location.pathname]);

  useEffect(() => {
    const origin =
      typeof window !== 'undefined' && window.location.origin
        ? window.location.origin
        : 'https://www.mc3smarttech.com';
    const canonical = `${origin}${location.pathname === '/' ? '' : location.pathname}`;
    const title = seo.title ? `${seo.title} | MC3 Smart Tech` : baseSeo.title;
    document.title = title;

    updateTag('meta[name=\"description\"]', {
      tagName: 'meta',
      name: 'description',
      content: seo.description || baseSeo.description,
    });

    updateTag('meta[name=\"keywords\"]', {
      tagName: 'meta',
      name: 'keywords',
      content: seo.keywords || baseSeo.keywords,
    });

    updateTag('meta[property=\"og:title\"]', {
      tagName: 'meta',
      property: 'og:title',
      content: title,
    });

    updateTag('meta[property=\"og:description\"]', {
      tagName: 'meta',
      property: 'og:description',
      content: seo.description || baseSeo.description,
    });

    updateTag('meta[property=\"og:type\"]', {
      tagName: 'meta',
      property: 'og:type',
      content: seo.type || baseSeo.type,
    });

    updateTag('meta[property=\"og:image\"]', {
      tagName: 'meta',
      property: 'og:image',
      content: seo.image || baseSeo.image,
    });

    updateTag('meta[property=\"og:url\"]', {
      tagName: 'meta',
      property: 'og:url',
      content: canonical,
    });

    updateTag('meta[name=\"twitter:card\"]', {
      tagName: 'meta',
      name: 'twitter:card',
      content: 'summary_large_image',
    });

    updateTag('meta[name=\"twitter:title\"]', {
      tagName: 'meta',
      name: 'twitter:title',
      content: title,
    });

    updateTag('meta[name=\"twitter:description\"]', {
      tagName: 'meta',
      name: 'twitter:description',
      content: seo.description || baseSeo.description,
    });

    updateTag('meta[name=\"twitter:image\"]', {
      tagName: 'meta',
      name: 'twitter:image',
      content: seo.image || baseSeo.image,
    });

    updateTag('link[rel=\"canonical\"]', {
      tagName: 'link',
      rel: 'canonical',
      href: canonical,
    });

    const ldJson = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'MC3 Smart Tech',
      url: canonical,
      sameAs: [
        'https://www.linkedin.com/company/mc3-smart-tech',
        'https://twitter.com/mc3smarttech',
      ],
      contactPoint: [
        {
          '@type': 'ContactPoint',
          telephone: '+1-518-394-4014',
          contactType: 'sales',
          areaServed: 'GB,US,EU',
          availableLanguage: ['English'],
        },
      ],
    };

    const script = document.getElementById('mc3-structured-data') || document.createElement('script');
    script.id = 'mc3-structured-data';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(ldJson);
    if (!script.parentNode) {
      document.head.appendChild(script);
    }
  }, [location.pathname, seo]);

  return null;
};

export default SeoManager;
